"use client";

import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  z: number;
  r: number;
  size: number;
  baseOpacity: number;
  twinkleOffset: number;
  twinkleSpeed: number;
  vx: number;
  vy: number;
  vz: number;
  layer: "surface" | "interior" | "spill" | "dust";
}

const PARTICLE_COUNT = 12000;
const SPHERE_RADIUS = 420;

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

interface SparkleOrbProps {
  color?: string;
}

export default function SparkleOrb({ color = "#ffffff" }: SparkleOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    let W = 0,
      H = 0,
      CX = 0,
      CY = 0,
      scale = 1;
    let raf: number;
    let rotY = 0,
      rotX = 0.15,
      t = 0;

    const setSize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.scale(dpr, dpr);
      CX = W / 2;
      CY = H / 2;
      const isMobile = window.innerWidth < 768;
      scale = (Math.min(W, H) / 850) * (isMobile ? 0.65 : 1);
    };
    setSize();

    const randDir = (): [number, number, number] => {
      const theta = 2 * Math.PI * Math.random();
      const phi = Math.acos(2 * Math.random() - 1);
      return [
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta),
      ];
    };

    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 3500 : PARTICLE_COUNT;
    const particles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const roll = Math.random();
      let layer: Particle["layer"],
        r: number,
        baseOpacity: number,
        size: number;
      let vx = 0,
        vy = 0,
        vz = 0;

      if (roll < 0.45) {
        layer = "surface";
        r = 0.87 + Math.random() * 0.15;
        baseOpacity = 0.5 + Math.random() * 0.5;
        size = 0.5 + Math.random() * 1.8;
      } else if (roll < 0.65) {
        layer = "interior";
        r = Math.pow(Math.random(), 0.5) * 0.87;
        baseOpacity = 0.12 + Math.random() * 0.38;
        size = 0.3 + Math.random() * 1.1;
      } else if (roll < 0.85) {
        layer = "spill";
        r = 1.0 + Math.pow(Math.random(), 1.5) * 0.6;
        const sf = 1 - (r - 1.0) / 0.6;
        baseOpacity = Math.random() * 0.5 * sf;
        size = 0.3 + Math.random() * 1.3;
        const sp = 0.00015 + Math.random() * 0.0003;
        const [dx, dy, dz] = randDir();
        vx = dx * sp;
        vy = dy * sp;
        vz = dz * sp;
      } else {
        layer = "dust";
        r = 0.3 + Math.random() * 1.5;
        baseOpacity = 0.04 + Math.random() * 0.18;
        size = 0.2 + Math.random() * 0.7;
      }

      const [dx, dy, dz] = randDir();
      particles.push({
        x: dx * r,
        y: dy * r,
        z: dz * r,
        r,
        size,
        baseOpacity,
        twinkleOffset: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.3 + Math.random() * 2.5,
        vx,
        vy,
        vz,
        layer,
      });
    }

    const rotatePoint = (
      px: number,
      py: number,
      pz: number,
      ry: number,
      rx: number
    ): [number, number, number] => {
      const cosY = Math.cos(ry),
        sinY = Math.sin(ry);
      const x1 = px * cosY + pz * sinY,
        z1 = -px * sinY + pz * cosY;
      const cosX = Math.cos(rx),
        sinX = Math.sin(rx);
      return [x1, py * cosX - z1 * sinX, py * sinX + z1 * cosX];
    };

    const projected = particles.map((_, i) => ({ i, rz: 0 }));
    const [cr, cg, cb] = hexToRgb(color);

    const draw = () => {
      raf = requestAnimationFrame(draw);
      t += 0.007;
      rotY += 0.0022;
      rotX = 0.15 + Math.sin(t * 0.18) * 0.05;
      ctx.clearRect(0, 0, W, H);

      const R = SPHERE_RADIUS * scale;
      const fov = 2.6;

      for (const p of particles) {
        if (p.layer === "spill") {
          p.x += p.vx;
          p.y += p.vy;
          p.z += p.vz;
          const ar = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
          if (ar > 1.65) {
            const [dx, dy, dz] = randDir();
            const nr = 0.98 + Math.random() * 0.05;
            p.x = dx * nr;
            p.y = dy * nr;
            p.z = dz * nr;
          }
        }
      }

      for (let i = 0; i < particles.length; i++) {
        const [, , rz] = rotatePoint(
          particles[i].x,
          particles[i].y,
          particles[i].z,
          rotY,
          rotX
        );
        projected[i].rz = rz;
      }
      projected.sort((a, b) => a.rz - b.rz);

      for (const { i, rz } of projected) {
        const p = particles[i];
        const [rx, ry] = rotatePoint(p.x, p.y, p.z, rotY, rotX);
        const ps = fov / (fov + rz);
        const sx = CX + rx * R * ps,
          sy = CY - ry * R * ps;
        if (sx < -80 || sx > W + 80 || sy < -80 || sy > H + 80) continue;

        const depth = (rz + 1.6) / 3.2;
        const twinkle =
          p.layer !== "dust"
            ? 0.6 + 0.4 * Math.sin(t * p.twinkleSpeed + p.twinkleOffset)
            : 1;
        let opacity = p.baseOpacity * (0.2 + depth * 0.8) * twinkle;

        if (p.layer === "spill") {
          const ar = Math.sqrt(p.x * p.x + p.y * p.y + p.z * p.z);
          const sd = (ar - 1.0) / 0.6;
          opacity *= sd <= 0 ? 1 : 1 - Math.min(1, sd);
        }

        opacity = Number.isFinite(opacity)
          ? Math.max(0, Math.min(1, opacity))
          : 0;
        if (opacity < 0.012) continue;

        const sz = Math.max(0.25, p.size * ps * scale * (0.5 + depth * 0.65));

        if (sz < 0.7) {
          ctx.globalAlpha = opacity;
          ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
          ctx.fillRect(
            Math.round(sx),
            Math.round(sy),
            Math.ceil(sz),
            Math.ceil(sz)
          );
        } else {
          const gr = Math.max(0.1, sz * 2.2);
          const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, gr);
          g.addColorStop(0, `rgba(${cr},${cg},${cb},${opacity.toFixed(4)})`);
          g.addColorStop(
            0.35,
            `rgba(${cr},${cg},${cb},${(opacity * 0.6).toFixed(4)})`
          );
          g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
          ctx.beginPath();
          ctx.arc(sx, sy, gr, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    };

    draw();

    const ro = new ResizeObserver(() => setSize());
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [color]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}
